import { AfterViewInit, Component, OnInit } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    NotificationService
} from '@vendure/admin-ui/core';

import { ExternalServicesSettingsInput, GtmMode } from '../types';

@Component({
    selector: 'external-services-form',
    template: `
    <vdr-card>
        <h3>Omnisend Email Marketing & SMS</h3>
        <div class="form-row">
            <vdr-form-field [label]="'Enabled'" class="form-item">
                <clr-toggle-wrapper>
                    <input
                        type="checkbox"
                        clrToggle
                        name="omnisendEnabled"
                        [formControl]="omnisendEnabledControl"
                    />
                </clr-toggle-wrapper>
            </vdr-form-field>
        </div>

        <div *ngIf="omnisendEnabledControl.value" class="form-row">
            <div class="form-item">
                <vdr-form-field [label]="'Brand ID'">
                    <input type="text" [formControl]="omnisendBrandIdControl">
                </vdr-form-field>
            </div>

            <div class="form-item" *ngIf="displayOmnisendApiKey">
                <vdr-form-field [label]="'API Key'">
                    <input type="password" [formControl]="omnisendApiKeyControl">
                </vdr-form-field>
            </div>
        </div>
    </vdr-card>
    <vdr-card>
        <h3>Google Tag Manager</h3>
        <div class="form-row">
            <vdr-form-field [label]="'Enabled'" class="form-item">
                <clr-toggle-wrapper>
                    <input
                        type="checkbox"
                        clrToggle
                        name="gtagEnabled"
                        [formControl]="gtagEnabledControl"
                    />
                </clr-toggle-wrapper>
            </vdr-form-field>
        </div>

        <div *ngIf="gtagEnabledControl.value" class="form-row">
            <div class="form-item">
                <vdr-form-field [label]="'GTM ID'">
                    <input type="text" [formControl]="gtagTrackingIdControl">
                </vdr-form-field>
            </div>
            <div class="form-item">
                <vdr-form-field [label]="'Mode'">
                    <select [formControl]="gtagModeControl">
                        <option [ngValue]="GtmMode.CLIENT_SIDE">Client-side</option>
                        <option [ngValue]="GtmMode.SERVER_SIDE">Server-side</option>
                    </select>
                </vdr-form-field>
            </div>
        </div>
    </vdr-card>
    <vdr-card>
        <h3>Clarity Manager</h3>
        <div class="form-row">
            <vdr-form-field [label]="'Enabled'" class="form-item">
                <clr-toggle-wrapper>
                    <input
                        type="checkbox"
                        clrToggle
                        name="clarityEnabled"
                        [formControl]="clarityEnabledControl"
                    />
                </clr-toggle-wrapper>
            </vdr-form-field>
        </div>

        <div *ngIf="clarityEnabledControl.value" class="form-row">
            <div class="form-item">
                <vdr-form-field [label]="'Clarity Tracking ID'">
                    <input type="text" [formControl]="clarityTrackingIdControl">
                </vdr-form-field>
            </div>
        </div>
    </vdr-card>
    <vdr-card>
        <h3>Tawk.to</h3>
        <div class="form-row">
            <vdr-form-field [label]="'Enabled'" class="form-item">
                <clr-toggle-wrapper>
                    <input
                        type="checkbox"
                        clrToggle
                        name="tawkioEnabled"
                        [formControl]="tawkioEnabledControl"
                    />
                </clr-toggle-wrapper>
            </vdr-form-field>
        </div>

        <div *ngIf="tawkioEnabledControl.value" class="form-row">
            <div class="form-item">
                <vdr-form-field [label]="'Embed Chat ID'">
                    <input
                        type="text"
                        [formControl]="tawkioChatIdControl"
                        placeholder="Property ID/Widget ID"
                        [attr.title]="'Created from <Property ID>/<Widget ID>'"
                    >
                </vdr-form-field>
            </div>
        </div>
    </vdr-card>
    `,
    styleUrls: ['../../common/ui/styles.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class ExternalServicesInputComponent
    implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
    readonly: boolean;
    config: StringCustomFieldConfig;
    formControl: FormControl;

    // Omnisend controls
    omnisendEnabledControl = new FormControl();
    omnisendBrandIdControl = new FormControl();
    omnisendApiKeyControl = new FormControl();

    // Gtag controls
    gtagEnabledControl = new FormControl();
    gtagTrackingIdControl = new FormControl();
    gtagModeControl = new FormControl();

    // Clarity controls
    clarityEnabledControl = new FormControl();
    clarityTrackingIdControl = new FormControl();

    // Tawkio controls
    tawkioEnabledControl = new FormControl();
    tawkioChatIdControl = new FormControl();

    externalServicesSettings: ExternalServicesSettingsInput;
    displayOmnisendApiKey = false;
    GtmMode = GtmMode;

    ngOnInit() {
        this.displayOmnisendApiKey = this.config.ui?.config?.displayOmnisendApikey ?? false;
        this.externalServicesSettings = this.formControl.value;
        this.ensureDefaultExternalServices();
        if (this.externalServicesSettings?.gtag && !this.externalServicesSettings.gtag.mode) {
            this.externalServicesSettings.gtag.mode = GtmMode.CLIENT_SIDE;
        }
        if (!this.displayOmnisendApiKey) {
            this.externalServicesSettings.omnisend.apiKey = undefined;
        }
        this.initializeFormControls();

        // React to external changes (e.g. when the parent formControl is updated on channel switch)
        this.formControl.valueChanges.subscribe(value => {
            if (!value) {
                return;
            }
            this.externalServicesSettings = {
                ...value
            };
            this.ensureDefaultExternalServices();
            if (this.externalServicesSettings?.gtag && !this.externalServicesSettings.gtag.mode) {
                this.externalServicesSettings.gtag.mode = GtmMode.CLIENT_SIDE;
            }
            if (!this.displayOmnisendApiKey) {
                this.externalServicesSettings.omnisend.apiKey = undefined;
            }
            this.initializeFormControls();
        });
    }

    ngAfterViewInit() {
        // Subscribe to Omnisend control changes
        this.omnisendEnabledControl.valueChanges.subscribe(this.onOmnisendEnabledChange);
        this.omnisendBrandIdControl.valueChanges.subscribe(this.onOmnisendBrandIdChange);
        this.omnisendApiKeyControl.valueChanges.subscribe(this.onOmnisendApiKeyChange);

        // Subscribe to Gtag control changes
        this.gtagEnabledControl.valueChanges.subscribe(this.onGtagEnabledChange);
        this.gtagTrackingIdControl.valueChanges.subscribe(this.onGtagTrackingIdChange);
        this.gtagModeControl.valueChanges.subscribe(this.onGtagModeChange);

        // Subscribe to Clarity control changes
        this.clarityEnabledControl.valueChanges.subscribe(this.onClarityEnabledChange);
        this.clarityTrackingIdControl.valueChanges.subscribe(this.onClarityTrackingIdChange);

        // Subscribe to Tawkio control changes
        this.tawkioEnabledControl.valueChanges.subscribe(this.onTawkioEnabledChange);
        this.tawkioChatIdControl.valueChanges.subscribe(this.onTawkioChatIdChange);
    }

    private initializeFormControls() {
        // Initialize Omnisend controls
        this.omnisendEnabledControl.setValue(this.externalServicesSettings?.omnisend?.enabled, { emitEvent: false });
        this.omnisendBrandIdControl.setValue(this.externalServicesSettings?.omnisend?.brandId, { emitEvent: false });
        this.omnisendApiKeyControl.setValue(this.externalServicesSettings?.omnisend?.apiKey, { emitEvent: false });

        // Initialize Gtag controls
        this.gtagEnabledControl.setValue(this.externalServicesSettings.gtag.enabled, { emitEvent: false });
        this.gtagTrackingIdControl.setValue(this.externalServicesSettings.gtag.trackingId, { emitEvent: false });
        this.gtagModeControl.setValue(this.externalServicesSettings.gtag.mode || GtmMode.CLIENT_SIDE, { emitEvent: false });

        // Initialize Clarity controls
        this.clarityEnabledControl.setValue(this.externalServicesSettings.clarity.enabled, { emitEvent: false });
        this.clarityTrackingIdControl.setValue(this.externalServicesSettings.clarity.trackingId, { emitEvent: false });

        // Initialize Tawkio controls
        this.tawkioEnabledControl.setValue(this.externalServicesSettings.tawkio.enabled, { emitEvent: false });
        const tawkioLegacy = this.externalServicesSettings.tawkio as any;
        this.tawkioChatIdControl.setValue(
            this.externalServicesSettings.tawkio.embedChatId ?? tawkioLegacy.chatId ?? '',
            { emitEvent: false },
        );
    }

    // Omnisend change handlers
    private onOmnisendEnabledChange = (value: boolean) => {
        if (this.externalServicesSettings?.omnisend?.enabled !== value) {
            this.externalServicesSettings.omnisend.enabled = value;
            this.updateValue();
        }
    };

    private onOmnisendBrandIdChange = (value: string) => {
        if (this.externalServicesSettings?.omnisend?.brandId !== value) {
            this.externalServicesSettings.omnisend.brandId = value;
            this.updateValue();
        }
    };

    private onOmnisendApiKeyChange = (value: string) => {
        if (this.externalServicesSettings?.omnisend?.apiKey !== value) {
            this.externalServicesSettings.omnisend.apiKey = value;
            this.updateValue();
        }
    };

    // Gtag change handlers
    private onGtagEnabledChange = (value: boolean) => {
        if (this.externalServicesSettings?.gtag?.enabled !== value) {
            this.externalServicesSettings.gtag.enabled = value;
            if (!value) {
                this.externalServicesSettings.gtag.trackingId = '';
                this.gtagTrackingIdControl?.setValue('');
            }
            this.updateValue();
        }
    };

    private onGtagTrackingIdChange = (value: string) => {
        if (this.externalServicesSettings.gtag.trackingId !== value) {
            this.externalServicesSettings.gtag.trackingId = value;
            this.updateValue();
        }
    };

    private onGtagModeChange = (value: GtmMode) => {
        const next = value || GtmMode.CLIENT_SIDE;
        if (this.externalServicesSettings.gtag.mode !== next) {
            this.externalServicesSettings.gtag.mode = next;
            this.updateValue();
        }
    };

    // Clarity change handlers
    private onClarityEnabledChange = (value: boolean) => {
        if (this.externalServicesSettings.clarity.enabled !== value) {
            this.externalServicesSettings.clarity.enabled = value;
            if (!value) {
                this.externalServicesSettings.clarity.trackingId = '';
                this.clarityTrackingIdControl?.setValue('');
            }
            this.updateValue();
        }
    };

    private onClarityTrackingIdChange = (value: string) => {
        if (this.externalServicesSettings.clarity.trackingId !== value) {
            this.externalServicesSettings.clarity.trackingId = value;
            this.updateValue();
        }
    };

    // Tawkio change handlers
    private onTawkioEnabledChange = (value: boolean) => {
        if (this.externalServicesSettings.tawkio.enabled !== value) {
            this.externalServicesSettings.tawkio.enabled = value;
            if (!value) {
                this.externalServicesSettings.tawkio.embedChatId = '';
                this.tawkioChatIdControl?.setValue('');
            }
            this.updateValue();
        }
    };

    private onTawkioChatIdChange = (value: string) => {
        if (this.externalServicesSettings.tawkio.embedChatId !== value) {
            this.externalServicesSettings.tawkio.embedChatId = value;
            this.updateValue();
        }
    };

    private ensureDefaultExternalServices() {
        this.externalServicesSettings = this.externalServicesSettings || {} as ExternalServicesSettingsInput;
        this.externalServicesSettings.omnisend = this.externalServicesSettings.omnisend || { enabled: false, brandId: '' };
        this.externalServicesSettings.gtag = this.externalServicesSettings.gtag || { enabled: false, trackingId: '', mode: GtmMode.CLIENT_SIDE };
        this.externalServicesSettings.clarity = this.externalServicesSettings.clarity || { enabled: false, trackingId: '' };
        this.externalServicesSettings.tawkio = this.externalServicesSettings.tawkio || { enabled: false, embedChatId: '' };
        if (this.externalServicesSettings.tawkio && !this.externalServicesSettings.tawkio.embedChatId) {
            const tawkioLegacy = this.externalServicesSettings.tawkio as any;
            this.externalServicesSettings.tawkio.embedChatId = tawkioLegacy.chatId ?? '';
        }
    }

    private updateValue() {
        // Do not emit to avoid triggering our own valueChanges subscription
        this.formControl.setValue(this.externalServicesSettings, { emitEvent: false });
        this.formControl.markAsDirty();
    }
}
